/* iter327L — Hydroxyproline / Col3 Support Protocol Card
 *
 * Phenotype-specific dietary protocol for endogenous Type-III collagen
 * synthesis support, based on Stewart 2024 (npj Breast Cancer).
 * Renders inside PhenotypeClassifier deep-research view when
 * `research.hydroxyproline_protocol` is present.
 */
import React, { useState } from "react";
import { Beaker, ChevronDown, ChevronUp, AlertTriangle, FlaskConical, Sparkles } from "lucide-react";

const EVIDENCE_BADGES = {
  A_basal_subtype: { label: "A — Basal subtype", color: "#10B981" },
  B_density_corr:  { label: "B — Density correlation", color: "#3B82F6" },
  C_mechanistic:   { label: "C — Mechanistic", color: "#A855F7" },
};

export const HydroxyprolineProtocolCard = ({ protocol, isDark = false }) => {
  const [expanded, setExpanded] = useState(false);
  if (!protocol || !protocol.applicable) return null;

  const badge = EVIDENCE_BADGES[protocol.evidence_strength] || { label: "—", color: "#64748B" };
  const txt = isDark ? "#F1F5F9" : "#1E293B";
  const sub = isDark ? "#94A3B8" : "#64748B";
  const bg = isDark ? "rgba(15,23,42,0.55)" : "#FFFFFF";
  const border = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";

  const dose = protocol.dose || {};
  const contras = protocol.active_contraindications || [];

  return (
    <div
      data-testid="hydroxyproline-protocol-card"
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        padding: 12,
        marginTop: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Sparkles size={14} style={{ color: "#10B981" }} />
        <strong style={{ fontSize: 13, color: txt }}>
          Hydroxyproline / Col3 Support Protocol
        </strong>
        <span
          data-testid="hyp-evidence-badge"
          style={{
            marginLeft: "auto",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            background: `${badge.color}1F`,
            color: badge.color,
            border: `1px solid ${badge.color}55`,
          }}
        >
          {badge.label}
        </span>
      </div>

      <div style={{ fontSize: 11, color: sub, marginBottom: 8, lineHeight: 1.5 }}>
        {protocol.indication_bg}
      </div>

      {/* Top-line dose summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 6,
          marginBottom: 8,
        }}
      >
        <DoseChip
          label="Глицин"
          value={`${dose.glycine_g_per_day} g/ден`}
          color="#10B981"
          isDark={isDark}
        />
        <DoseChip
          label="Пролин"
          value={`${dose.proline_g_per_day} g/ден`}
          color="#22D3EE"
          isDark={isDark}
        />
        <DoseChip
          label="Витамин C"
          value={`${dose.vit_c_mg_per_day} mg/ден`}
          color="#F97316"
          isDark={isDark}
        />
        <DoseChip
          label="Ferritin"
          value={dose.fe_target_ferritin || "—"}
          color="#EF4444"
          isDark={isDark}
        />
      </div>

      {/* Active contraindications */}
      {contras.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {contras.map((c, idx) => (
            <div
              key={idx}
              data-testid={`hyp-contra-${c.type}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                padding: "6px 8px",
                marginBottom: 4,
                borderRadius: 6,
                fontSize: 10,
                lineHeight: 1.5,
                background: c.severity === "absolute" ? "#FEF2F2" : "#FFFBEB",
                border: `1px solid ${c.severity === "absolute" ? "#FCA5A5" : "#FDE68A"}`,
                color: "#7F1D1D",
              }}
            >
              <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong style={{ textTransform: "uppercase", marginRight: 4 }}>
                  [{c.severity}]
                </strong>
                {c.message_bg}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Phenotype caveats */}
      {(protocol.phenotype_caveats_bg || []).length > 0 && (
        <ul
          data-testid="hyp-caveats"
          style={{
            margin: 0,
            paddingLeft: 16,
            fontSize: 10,
            color: sub,
            lineHeight: 1.6,
          }}
        >
          {protocol.phenotype_caveats_bg.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      )}

      {/* Toggle for full cofactor + substrate detail */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="hyp-toggle-detail"
        style={{
          marginTop: 8,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          color: "#10B981",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? "Скрий детайли" : "Покажи кофактори & субстрати"}
      </button>

      {expanded && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          <Section
            title="🧬 Субстрати"
            items={protocol.substrates}
            getKey={(it) => it.id}
            renderItem={(it) => (
              <DetailRow
                title={it.name_bg}
                target={it.daily_target}
                role={it.role_bg}
                sources={it.food_sources_bg}
                evidence={it.evidence}
                isDark={isDark}
              />
            )}
            isDark={isDark}
            testid="hyp-section-substrates"
          />
          <Section
            title="🧪 Кофактори"
            items={protocol.cofactors}
            getKey={(it) => it.id}
            renderItem={(it) => (
              <DetailRow
                title={it.name_bg}
                target={it.daily_target}
                role={it.role_bg}
                sources={it.food_sources_bg}
                evidence={it.evidence}
                isDark={isDark}
              />
            )}
            isDark={isDark}
            testid="hyp-section-cofactors"
          />
          <Section
            title="🛡 Анти-aligning фактори"
            items={protocol.anti_aligning_factors}
            getKey={(it, i) => i}
            renderItem={(it) => (
              <div style={{ fontSize: 11, color: txt, lineHeight: 1.5 }}>
                <strong>{it.name_bg}</strong>
                <div style={{ color: sub, marginTop: 2 }}>{it.rationale_bg}</div>
                {it.pmid && it.pmid.length > 0 && (
                  <div style={{ fontSize: 9, color: sub, marginTop: 2 }}>
                    PMID: {it.pmid.join(", ")}
                  </div>
                )}
              </div>
            )}
            isDark={isDark}
            testid="hyp-section-antialigning"
          />

          <div
            style={{
              fontSize: 10,
              color: sub,
              padding: "6px 8px",
              borderRadius: 6,
              background: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
              borderLeft: "3px solid #10B981",
            }}
            data-testid="hyp-primary-reference"
          >
            <strong style={{ color: "#10B981" }}>📚 Първичен референс:</strong>{" "}
            {protocol.primary_reference_bg}
          </div>
        </div>
      )}
    </div>
  );
};

const DoseChip = ({ label, value, color, isDark }) => (
  <div
    style={{
      padding: "4px 8px",
      borderRadius: 6,
      background: `${color}10`,
      border: `1px solid ${color}33`,
    }}
  >
    <div style={{ fontSize: 9, color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </div>
    <div style={{ fontSize: 12, fontWeight: 700, color }}>{value}</div>
  </div>
);

const Section = ({ title, items, getKey, renderItem, isDark, testid }) => (
  <div data-testid={testid}>
    <div style={{
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: isDark ? "#94A3B8" : "#64748B",
      marginBottom: 4,
    }}>
      {title}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(items || []).map((it, i) => (
        <div
          key={getKey(it, i)}
          style={{
            padding: "6px 8px",
            borderRadius: 6,
            background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
          }}
        >
          {renderItem(it, i)}
        </div>
      ))}
    </div>
  </div>
);

const DetailRow = ({ title, target, role, sources, evidence, isDark }) => {
  const txt = isDark ? "#F1F5F9" : "#1E293B";
  const sub = isDark ? "#94A3B8" : "#64748B";
  return (
    <div style={{ fontSize: 11, lineHeight: 1.5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ color: txt }}>{title}</strong>
        <span style={{ fontSize: 10, color: "#10B981", fontWeight: 600 }}>{target}</span>
      </div>
      <div style={{ color: sub, marginTop: 2 }}>{role}</div>
      {sources && sources.length > 0 && (
        <div style={{ fontSize: 10, color: sub, marginTop: 4 }}>
          🍴 {sources.slice(0, 4).join(" • ")}
        </div>
      )}
      {evidence && (
        <div style={{ fontSize: 9, color: sub, marginTop: 2, fontStyle: "italic" }}>
          {evidence}
        </div>
      )}
    </div>
  );
};

export default HydroxyprolineProtocolCard;
